import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';

import { fadeTransition, FadeVariant, fadeVariants } from '@pkg/motion';
import { designColor, iconSize, styledLayout, styledMedia, styledReset } from '@pkg/primitives';

import { TooltipInteriorVariant, tooltipInteriorVariants } from './InputRadio.motion';

const radioSize = 20;
const radioPipSize = 8;

const labelColorTransition = css`
    transition-property: color;
    transition-duration: ${({ theme }) => theme.transitions.speeds.fast}s;
    transition-timing-function: ${({ theme }) => theme.transitions.easesCss.cubic};
`;

// `$disabled` needs to:
// Dim typography colors (don't use opacity, as we still want `Tooltip` interactive).
// Change `cursor` to only affect certain elements (leave `Tooltip` alone).
// Prevent `:focus/hover/active` interactions on everything except `Tooltip`.
// Dim color of `RadioVisual`.

export const StyledInputRadio = styled.div`
    position: relative;
`;

function getCursor({ $disabled, $loading }: { $disabled?: boolean; $loading?: boolean }) {
    if ($loading) return 'wait';
    return $disabled ? 'not-allowed' : 'pointer';
}

export const Label = styled.label<{ $disabled?: boolean; $loading?: boolean; $padded?: boolean }>`
    --yat_input-radio-label-color: ${({ theme, $disabled }) =>
        $disabled ? theme.shades.white['80'] : theme.shades.white['100']};
    --yat_radio-visual-color: ${({ theme }) => theme.shades.white['80']};
    --yat_radio-visual-bg-color: transparent;
    --yat_radio-visual-radius: 50%;

    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: 1fr 0 auto;
    gap: 20px;
    align-items: center;
    padding: ${({ $padded }) => ($padded ? '16px' : 0)};
    cursor: ${({ $disabled, $loading }) => getCursor({ $disabled, $loading })};

    &:hover {
        --yat_radio-visual-color: ${({ theme }) => theme.shades.white['80']};
        --yat_radio-visual-bg-color: ${({ theme }) => theme.shades.white['10']};
    }

    &:active {
        --yat_radio-visual-color: ${({ theme }) => theme.shades.white['50']};
        --yat_radio-visual-bg-color: ${({ theme }) => theme.shades.white['4']};
    }
`;

///
/// Content

export const Content = styled.div`
    // styles
`;

export const ContentLabelWrapper = styled.span`
    display: flex;
    gap: 8px;
    align-items: center;
    color: var(--yat_input-radio-label-color);
`;

export const LabelIcon = styled.span`
    ${styledMedia.iconWrapper}
    ${labelColorTransition}
    width: ${iconSize.small}px;
    height: ${iconSize.small}px;
    color: currentColor;
`;

export const LabelText = styled.span`
    ${styledReset.typography}
    ${labelColorTransition}
    display: block;
    font-family: 'Alliance No.1';
    font-size: 16px;
    font-weight: 700;
    line-height: 1.2;
    color: currentColor;
`;

export const TooltipIcon = styled.span<{ $disabled?: boolean }>`
    ${styledMedia.iconWrapper}
    width: ${iconSize.small}px;
    height: ${iconSize.small}px;
    color: currentColor;
    opacity: 0.5;
    transition-property: color, opacity;
    transition-duration: ${({ theme }) => theme.transitions.speeds.fast}s;
    transition-timing-function: ${({ theme }) => theme.transitions.easesCss.cubic};

    &:hover {
        opacity: ${({ $disabled }) => ($disabled ? 0.5 : 0.8)};
    }

    &:active {
        opacity: 0.5;
    }
`;

export const InfoText = styled.p`
    ${styledReset.typography}
    margin-top: 10px;
    font-family: 'Alliance No.1';
    font-size: 14px;
    line-height: 1.3;
    color: ${({ theme }) => theme.shades.white['50']};
`;

///
/// Radio

export const RadioElement = styled.input.attrs({ type: 'radio' })`
    ${styledLayout.visuallyHidden}
`;

export const RadioVisual = styled.div<{ $loading?: boolean }>`
    position: relative;
    width: ${radioSize}px;
    height: ${radioSize}px;
    background-color: var(--yat_radio-visual-bg-color);
    box-shadow: inset 0 0 0 2px var(--yat_radio-visual-color);
    border-radius: var(--yat_radio-visual-radius);
    transition-property: background-color, box-shadow;
    transition-duration: ${({ theme }) => theme.transitions.speeds.fast}s;
    transition-timing-function: ${({ theme }) => theme.transitions.easesCss.cubic};

    ${RadioElement}:checked + & {
        --yat_radio-visual-color: ${({ theme }) => theme.colors.purple};
    }

    ${({ $loading }) => {
        if ($loading) {
            // Need to "fade out" the box-shadow, otherwise
            // we get gross aliasing when the Spinner is rendered.
            return css`
                --yat_radio-visual-color: transparent;
            `;
        }
    }}

    ${Label}:hover ${RadioElement}:checked + & {
        --yat_radio-visual-bg-color: ${({ theme }) => theme.shades.purple['20']};
    }

    ${Label}:active ${RadioElement}:checked + & {
        --yat_radio-visual-bg-color: ${({ theme }) => theme.shades.purple['5']};
    }

    &::before,
    &::after {
        content: '';
        position: absolute;
        margin: auto;
        display: block;
        border-radius: var(--yat_radio-visual-radius);
        opacity: 0;
        transition-duration: ${({ theme }) => theme.transitions.speeds.fast}s;
        transition-timing-function: ${({ theme }) => theme.transitions.easesCss.cubic};
    }

    ///
    /// Focus ring

    &::before {
        top: -6px;
        right: -6px;
        bottom: -6px;
        left: -6px;
        border: 2px solid ${({ theme }) => theme.shades.white['10']};
        transition-property: opacity;
    }

    ${RadioElement}:focus-visible + &::before {
        opacity: 1;
    }

    ///
    /// Pip

    &::after {
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        width: ${radioPipSize}px;
        height: ${radioPipSize}px;
        background-color: var(--yat_radio-visual-color);
        transform: scale(0);
        transition-property: background-color, opacity, transform;
    }

    ${RadioElement}:checked + &::after {
        opacity: 1;
        transform: scale(1);
    }

    ///
    /// Loading Spinner
    > div[data-spinner='simple'] {
        ${styledLayout.positionCover()};
        font-size: ${radioSize}px;
        color: ${designColor.brandPurple};
    }
`;

///
/// Tooltip

export const TooltipWrapper = styled(motion.div).attrs({
    variants: fadeVariants,
    initial: FadeVariant.Hidden,
    animate: FadeVariant.Show,
    exit: FadeVariant.Hidden,
    transition: fadeTransition,
})`
    position: absolute;
    z-index: 2;
    // TODO: Until we can implement a better Tooltip,
    // we are deviating from the designs and rendering the
    // Tooltip below its "anchor".
    top: calc(100% - 10px);
    right: 10px;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletSmall}) {
        left: 10px;
        right: auto;
    }

    /*
    top: 50%;
    // TODO: So far, we only support a right-align position.
    right: 0;
    transform: translate(100%, -50%);
    */
`;

export const TooltipInterior = styled(motion.div).attrs({
    variants: tooltipInteriorVariants,
    initial: TooltipInteriorVariant.Hidden,
    animate: TooltipInteriorVariant.Show,
    exit: TooltipInteriorVariant.Hidden,
})`
    // This element needs to exist so that we can leverage
    // framer-motion to perform a enter/exit animation on
    // the transform > translateX property. Since the parent
    // requires transform for positioning, we have to
    // split the animation across two elements.
`;
